export type VariantName = 'original' | 'grayscale' | 'contrast' | 'adaptive' | 'deskew' | 'perspective'
export type NormalizedRect = { x: number; y: number; width: number; height: number }
export type TemplateZone = { path: string; rect: NormalizedRect }
export type CropSet = { path: string; variants: Partial<Record<VariantName, Blob>>; inkDensity: number; crossedOut: boolean }

const parentZones: TemplateZone[] = [
  { path: 'hoja', rect: { x: .03, y: .025, width: .13, height: .07 } },
  { path: 'anio', rect: { x: .82, y: .025, width: .15, height: .07 } },
  { path: 'padre.nombre', rect: { x: .04, y: .105, width: .29, height: .07 } },
  { path: 'padre.anilla', rect: { x: .34, y: .105, width: .25, height: .07 } },
  { path: 'padre.plumaje', rect: { x: .60, y: .105, width: .36, height: .07 } },
  { path: 'madre.nombre', rect: { x: .04, y: .185, width: .29, height: .07 } },
  { path: 'madre.anilla', rect: { x: .34, y: .185, width: .25, height: .07 } },
  { path: 'madre.plumaje', rect: { x: .60, y: .185, width: .36, height: .07 } }
]

export function templateZones(rowCount = 12): TemplateZone[] {
  const zones = [...parentZones]
  const top = .31
  const usable = .65
  const rowHeight = usable / rowCount
  for (let row = 0; row < rowCount; row += 1) {
    const y = top + row * rowHeight
    zones.push(
      { path: `pichones.${row}.anilla`, rect: { x: .025, y, width: .235, height: rowHeight } },
      { path: `pichones.${row}.plumaje`, rect: { x: .265, y, width: .245, height: rowHeight } },
      { path: `pichones.${row}.sexo`, rect: { x: .515, y, width: .115, height: rowHeight } },
      { path: `pichones.${row}.observaciones`, rect: { x: .635, y, width: .34, height: rowHeight } }
    )
  }
  return zones
}

function canvas(width: number, height: number) {
  const result = document.createElement('canvas')
  result.width = Math.max(1, Math.round(width))
  result.height = Math.max(1, Math.round(height))
  return result
}

async function decode(file: File) {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  const landscape = bitmap.width >= bitmap.height
  const scale = Math.min(1, 1900 / Math.max(bitmap.width, bitmap.height))
  const result = canvas((landscape ? bitmap.width : bitmap.height) * scale, (landscape ? bitmap.height : bitmap.width) * scale)
  const context = result.getContext('2d', { willReadFrequently: true })!
  context.fillStyle = 'white'
  context.fillRect(0, 0, result.width, result.height)
  if (landscape) context.drawImage(bitmap, 0, 0, result.width, result.height)
  else {
    context.translate(result.width, 0)
    context.rotate(Math.PI / 2)
    context.drawImage(bitmap, 0, 0, result.height, result.width)
  }
  bitmap.close()
  return result
}

function grayscale(source: HTMLCanvasElement) {
  const result = canvas(source.width, source.height)
  const context = result.getContext('2d', { willReadFrequently: true })!
  context.drawImage(source, 0, 0)
  const image = context.getImageData(0, 0, result.width, result.height)
  for (let index = 0; index < image.data.length; index += 4) {
    const value = Math.round(.299 * image.data[index] + .587 * image.data[index + 1] + .114 * image.data[index + 2])
    image.data[index] = image.data[index + 1] = image.data[index + 2] = value
  }
  context.putImageData(image, 0, 0)
  return result
}

function contrast(source: HTMLCanvasElement) {
  const result = grayscale(source)
  const context = result.getContext('2d', { willReadFrequently: true })!
  const image = context.getImageData(0, 0, result.width, result.height)
  const histogram = new Uint32Array(256)
  for (let index = 0; index < image.data.length; index += 4) histogram[image.data[index]] += 1
  const pixels = result.width * result.height
  let low = 0; let high = 255; let accumulated = 0
  while (low < 255 && accumulated + histogram[low] < pixels * .02) accumulated += histogram[low++]
  accumulated = 0
  while (high > 0 && accumulated + histogram[high] < pixels * .02) accumulated += histogram[high--]
  const range = Math.max(1, high - low)
  for (let index = 0; index < image.data.length; index += 4) {
    const value = Math.max(0, Math.min(255, (image.data[index] - low) * 255 / range))
    image.data[index] = image.data[index + 1] = image.data[index + 2] = value
  }
  context.putImageData(image, 0, 0)
  return result
}

function adaptive(source: HTMLCanvasElement) {
  const gray = grayscale(source)
  const context = gray.getContext('2d', { willReadFrequently: true })!
  const image = context.getImageData(0, 0, gray.width, gray.height)
  const { width, height } = gray
  const integral = new Float64Array((width + 1) * (height + 1))
  for (let y = 1; y <= height; y += 1) {
    let row = 0
    for (let x = 1; x <= width; x += 1) {
      row += image.data[((y - 1) * width + x - 1) * 4]
      integral[y * (width + 1) + x] = integral[(y - 1) * (width + 1) + x] + row
    }
  }
  const radius = Math.max(9, Math.round(Math.min(width, height) / 70))
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
    const x1 = Math.max(0, x - radius); const y1 = Math.max(0, y - radius)
    const x2 = Math.min(width - 1, x + radius); const y2 = Math.min(height - 1, y + radius)
    const count = (x2 - x1 + 1) * (y2 - y1 + 1)
    const sum = integral[(y2 + 1) * (width + 1) + x2 + 1] - integral[y1 * (width + 1) + x2 + 1] - integral[(y2 + 1) * (width + 1) + x1] + integral[y1 * (width + 1) + x1]
    const value = image.data[(y * width + x) * 4] < sum / count - 12 ? 0 : 255
    const index = (y * width + x) * 4
    image.data[index] = image.data[index + 1] = image.data[index + 2] = value
  }
  context.putImageData(image, 0, 0)
  return gray
}

function inkDensity(source: HTMLCanvasElement) {
  const data = source.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, source.width, source.height).data
  let dark = 0
  for (let index = 0; index < data.length; index += 16) if (data[index] < 100) dark += 1
  return dark / Math.max(1, data.length / 16)
}

function hasStrikeThrough(source: HTMLCanvasElement) {
  const { width, height } = source
  const data = source.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, width, height).data
  for (let y = Math.round(height * .22); y < height * .78; y += 1) {
    let dark = 0
    for (let x = 0; x < width; x += 1) if (data[(y * width + x) * 4] < 70) dark += 1
    if (dark / width > .58) return true
  }
  return false
}

function estimateSkew(binary: HTMLCanvasElement) {
  const { width, height } = binary
  const data = binary.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, width, height).data
  let bestAngle = 0; let bestScore = -1
  for (let angle = -4; angle <= 4; angle += .5) {
    const rows = new Uint32Array(height + 80)
    const tangent = Math.tan(angle * Math.PI / 180)
    for (let y = 0; y < height; y += 4) for (let x = 0; x < width; x += 4) {
      if (data[(y * width + x) * 4] < 80) rows[Math.max(0, Math.min(rows.length - 1, Math.round(y + tangent * (x - width / 2) + 40)))] += 1
    }
    let score = 0
    for (const value of rows) score += value * value
    if (score > bestScore) { bestScore = score; bestAngle = angle }
  }
  return bestAngle
}

function rotate(source: HTMLCanvasElement, degrees: number) {
  if (Math.abs(degrees) < .25) return source
  const result = canvas(source.width, source.height)
  const context = result.getContext('2d')!
  context.fillStyle = 'white'; context.fillRect(0, 0, result.width, result.height)
  context.translate(result.width / 2, result.height / 2)
  context.rotate(-degrees * Math.PI / 180)
  context.drawImage(source, -source.width / 2, -source.height / 2)
  return result
}

function trimBorders(source: HTMLCanvasElement) {
  const marginX = Math.round(source.width * .015); const marginY = Math.round(source.height * .02)
  const result = canvas(source.width - marginX * 2, source.height - marginY * 2)
  result.getContext('2d')!.drawImage(source, marginX, marginY, result.width, result.height, 0, 0, result.width, result.height)
  return result
}

type Point = { x: number; y: number }

function polygonArea(points: Point[]) {
  return Math.abs(points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length]
    return sum + point.x * next.y - next.x * point.y
  }, 0) / 2)
}

function detectPageQuad(source: HTMLCanvasElement): [Point, Point, Point, Point] {
  const gray = grayscale(source)
  const { width, height } = gray
  const data = gray.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, width, height).data
  const cornerValues = [
    data[0], data[(width - 1) * 4], data[((height - 1) * width) * 4], data[(height * width - 1) * 4]
  ].sort((a, b) => a - b)
  const background = (cornerValues[1] + cornerValues[2]) / 2
  const candidates: Point[] = []
  const step = Math.max(2, Math.round(Math.max(width, height) / 700))
  for (let y = 0; y < height; y += step) for (let x = 0; x < width; x += step) {
    const light = data[(y * width + x) * 4]
    if ((background < 205 && light > Math.max(145, background + 22)) || (background >= 205 && light < background - 30 && light > 120)) candidates.push({ x, y })
  }
  if (candidates.length < 100) return [{ x: 0, y: 0 }, { x: width - 1, y: 0 }, { x: width - 1, y: height - 1 }, { x: 0, y: height - 1 }]
  const topLeft = candidates.reduce((best, point) => point.x + point.y < best.x + best.y ? point : best)
  const bottomRight = candidates.reduce((best, point) => point.x + point.y > best.x + best.y ? point : best)
  const topRight = candidates.reduce((best, point) => point.x - point.y > best.x - best.y ? point : best)
  const bottomLeft = candidates.reduce((best, point) => point.x - point.y < best.x - best.y ? point : best)
  const quad: [Point, Point, Point, Point] = [topLeft, topRight, bottomRight, bottomLeft]
  return polygonArea(quad) > width * height * .35 ? quad : [{ x: 0, y: 0 }, { x: width - 1, y: 0 }, { x: width - 1, y: height - 1 }, { x: 0, y: height - 1 }]
}

function solve(matrix: number[][], vector: number[]) {
  const size = vector.length
  const augmented = matrix.map((row, index) => [...row, vector[index]])
  for (let column = 0; column < size; column += 1) {
    let pivot = column
    for (let row = column + 1; row < size; row += 1) if (Math.abs(augmented[row][column]) > Math.abs(augmented[pivot][column])) pivot = row
    ;[augmented[column], augmented[pivot]] = [augmented[pivot], augmented[column]]
    const divisor = augmented[column][column] || 1e-9
    for (let cell = column; cell <= size; cell += 1) augmented[column][cell] /= divisor
    for (let row = 0; row < size; row += 1) if (row !== column) {
      const factor = augmented[row][column]
      for (let cell = column; cell <= size; cell += 1) augmented[row][cell] -= factor * augmented[column][cell]
    }
  }
  return augmented.map(row => row[size])
}

function rectifyPerspective(source: HTMLCanvasElement) {
  const quad = detectPageQuad(source)
  const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y)
  const width = Math.max(400, Math.round(Math.max(distance(quad[0], quad[1]), distance(quad[3], quad[2]))))
  const height = Math.max(250, Math.round(Math.max(distance(quad[0], quad[3]), distance(quad[1], quad[2]))))
  const uv = [[0, 0], [1, 0], [1, 1], [0, 1]]
  const matrix: number[][] = []; const vector: number[] = []
  uv.forEach(([u, v], index) => {
    const point = quad[index]
    matrix.push([u, v, 1, 0, 0, 0, -point.x * u, -point.x * v]); vector.push(point.x)
    matrix.push([0, 0, 0, u, v, 1, -point.y * u, -point.y * v]); vector.push(point.y)
  })
  const [a, b, c, d, e, f, g, h] = solve(matrix, vector)
  const input = source.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, source.width, source.height)
  const outputCanvas = canvas(width, height)
  const outputContext = outputCanvas.getContext('2d', { willReadFrequently: true })!
  const output = outputContext.createImageData(width, height)
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
    const u = x / Math.max(1, width - 1); const v = y / Math.max(1, height - 1)
    const denominator = g * u + h * v + 1
    const sx = Math.max(0, Math.min(source.width - 1, Math.round((a * u + b * v + c) / denominator)))
    const sy = Math.max(0, Math.min(source.height - 1, Math.round((d * u + e * v + f) / denominator)))
    const sourceIndex = (sy * source.width + sx) * 4; const targetIndex = (y * width + x) * 4
    output.data[targetIndex] = input.data[sourceIndex]; output.data[targetIndex + 1] = input.data[sourceIndex + 1]
    output.data[targetIndex + 2] = input.data[sourceIndex + 2]; output.data[targetIndex + 3] = 255
  }
  outputContext.putImageData(output, 0, 0)
  return outputCanvas
}

function crop(source: HTMLCanvasElement, rect: NormalizedRect, padding = 5) {
  const sx = Math.max(0, Math.round(rect.x * source.width) - padding)
  const sy = Math.max(0, Math.round(rect.y * source.height) - padding)
  const sw = Math.min(source.width - sx, Math.round(rect.width * source.width) + padding * 2)
  const sh = Math.min(source.height - sy, Math.round(rect.height * source.height) + padding * 2)
  const scale = Math.max(1, Math.min(3, 900 / Math.max(sw, sh)))
  const result = canvas(sw * scale, sh * scale)
  result.getContext('2d')!.drawImage(source, sx, sy, sw, sh, 0, 0, result.width, result.height)
  return result
}

function blob(source: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => source.toBlob(value => value ? resolve(value) : reject(new Error('No se pudo generar un recorte OCR.')), 'image/png'))
}

export async function preprocessSheet(file: File) {
  const decoded = await decode(file)
  const trimmed = trimBorders(decoded)
  const rectified = rectifyPerspective(trimmed)
  const firstAdaptive = adaptive(rectified)
  const angle = estimateSkew(firstAdaptive)
  const deskewed = rotate(rectified, angle)
  const perspective = trimBorders(deskewed)
  const variants = {
    original: trimmed,
    grayscale: grayscale(perspective),
    contrast: contrast(perspective),
    adaptive: adaptive(perspective),
    deskew: deskewed,
    perspective
  } satisfies Record<VariantName, HTMLCanvasElement>
  const crops: CropSet[] = []
  for (const zone of templateZones()) {
    const adaptiveCrop = crop(variants.adaptive, zone.rect)
    const density = inkDensity(adaptiveCrop)
    if (zone.path.startsWith('pichones.') && density < .012) continue
    crops.push({
      path: zone.path,
      inkDensity: density,
      crossedOut: hasStrikeThrough(adaptiveCrop),
      variants: {
        original: await blob(crop(variants.original, zone.rect)),
        contrast: await blob(crop(variants.contrast, zone.rect)),
        adaptive: await blob(adaptiveCrop),
        deskew: await blob(crop(variants.deskew, zone.rect)),
        perspective: await blob(crop(variants.perspective, zone.rect))
      }
    })
  }
  return { crops, skewDegrees: angle, variants: Object.keys(variants) as VariantName[] }
}
